import { forwardRef, TextareaHTMLAttributes } from 'react';

/**
 * Textarea component matching Bootstrap .form-control styles EXACTLY
 *
 * Source of truth from bootstrap-3.4.1.min.css:
 *
 * textarea.form-control {
 *   height: auto;
 *   (other .form-control styles apply)
 * }
 *
 * Bootstrap .form-control base styles:
 *   display: block;
 *   width: 100%;
 *   padding: 6px 12px;
 *   font-size: 14px;
 *   line-height: 1.42857143;
 *   color: #555;
 *   background-color: #fff;
 *   border: 1px solid #ccc;
 *   border-radius: 4px;
 *
 * NO CHANGES to logic, behavior, or structure - only visual parity.
 */

export interface UiTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  hasError?: boolean;
}

export const UiTextarea = forwardRef<HTMLTextAreaElement, UiTextareaProps>(
  ({ hasError, className = '', ...props }, ref) => {
    // Base classes matching .form-control from Bootstrap (height: auto for textarea)
    const baseClasses = [
      'block', // display: block
      'w-full', // width: 100%
      'max-w-full', // prevent resize overflow beyond parent
      'resize-y', // allow vertical resize only
      'px-3', // padding: 6px 12px (horizontal = 12px)
      'py-[6px]', // padding: 6px 12px (vertical = 6px)
      'text-angular-base', // font-size: 14px
      'leading-angular-base', // line-height: 1.42857143
      'text-[#555]', // color: #555
      'bg-white', // background-color: #fff
      'border', // border: 1px solid
      'border-solid',
      'rounded-angular', // border-radius: 4px
      'transition-all', // transition: border-color, box-shadow ease-in-out .15s
      'duration-150',
      'ease-in-out',
    ].join(' ');

    const borderClasses = hasError
      ? 'border-angular-error-highlight'
      : 'border-angular-border-input focus:border-angular-focus';

    const shadowClasses = hasError
      ? 'focus:shadow-angular-input-error-focus'
      : 'shadow-angular-input focus:shadow-angular-input-focus';

    // Disabled/readonly state matching Bootstrap 3 .form-control[disabled]
    // BS3: background-color: #eee; opacity: 1; cursor: not-allowed;
    const disabledClasses =
      props.disabled || props.readOnly
        ? hasError
          ? '!bg-angular-bg-disabled !text-[#555] !cursor-not-allowed !opacity-100'
          : '!bg-angular-bg-disabled !text-[#555] !cursor-not-allowed !opacity-100 !border-angular-border-input'
        : '';

    const focusClasses = 'focus:outline-none';

    return (
      <textarea
        ref={ref}
        aria-invalid={hasError || undefined}
        className={`${baseClasses} ${borderClasses} ${shadowClasses} ${disabledClasses} ${focusClasses} ${className}`}
        {...props}
      />
    );
  }
);

UiTextarea.displayName = 'UiTextarea';
